import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import {
  SimpleGrid,
  Box,
  Text,
  Flex,
  Spinner,
  Badge,
} from '@chakra-ui/react';

import * as FiIcons from 'react-icons/fi';

import Filter from './actions/Filter';
import { getStatistics } from '../../../../store/dashboard/statisticsSlice';

import theme from '../../../global/theme';
import Card from '../../../shared/card/Card';
import Table from '../../../shared/table/Table';

const TRANSACTION_TYPE_LABEL = { deposit: 'إيداع', purchase: 'شراء', refund: 'استرجاع' };
const TRANSACTION_STATUS_COLOR = { pending: 'yellow', completed: 'green', failed: 'red' };
const TRANSACTION_STATUS_LABEL = { pending: 'قيد الانتظار', completed: 'مكتملة', failed: 'فشلت' };

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const statistics = useSelector(state => state.statistics);
  const auth = useSelector(state => state.auth);

  const [filter, setFilter] = useState({});

  useEffect(() => {
    dispatch(getStatistics({ filter: JSON.stringify(filter) }));
  }, [dispatch, filter]);

  const canViewStats =
    auth.user?.type === 'admin' &&
    auth.user?.role?.permissions?.includes('manage_dashboard');

  return (
    <Box>
      <Box mb={6}>
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={4}
        >
          <Box>
            <Text
              fontSize={{ base: '24px', md: '28px' }}
              fontWeight="800"
              color={theme.dark}
              mb={1}
            >
              التقارير العامة
            </Text>

            <Text color={theme.text} fontSize="14px">
              نظرة عامة على أداء الحساب والإحصائيات الرئيسية
            </Text>
          </Box>

          <Filter
            isLoading={statistics.isLoading}
            handleFilter={values => setFilter(values)}
            handleClearFilter={() => setFilter({})}
          />
        </Flex>
      </Box>

      {statistics.isLoading && !statistics.data ? (
        <Flex
          minH="260px"
          bg={theme.light}
          border={`1px solid ${theme.border}`}
          borderRadius="24px"
          boxShadow={theme.shadow}
          align="center"
          justify="center"
          direction="column"
          gap={4}
          mb={6}
        >
          <Spinner color={theme.primary} thickness="4px" size="xl" />
          <Text color={theme.text} fontWeight="600">
            جاري تحميل الإحصائيات...
          </Text>
        </Flex>
      ) : null}

      {canViewStats && (
        <Box
          bg={theme.light}
          border={`1px solid ${theme.border}`}
          borderRadius="24px"
          boxShadow={theme.shadow}
          p={{ base: 4, md: 6 }}
          mb={6}
        >
          <Flex justify="space-between" align="center" mb={5}>
            <Box>
              <Text fontWeight="800" color={theme.dark} fontSize="18px">
                الإحصائيات الرئيسية
              </Text>
              <Text color={theme.text} fontSize="13px">
                ملخص سريع لأهم مؤشرات الأداء
              </Text>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            <Card
              icon={<FiIcons.FiUsers size={22} />}
              title="إجمالي المستخدمين"
              count={statistics.data?.usersCount ?? '-'}
            />

            <Card
              icon={<FiIcons.FiUserCheck size={22} />}
              title="المستخدمين النشطين"
              count={statistics.data?.activeUsersCount ?? '-'}
            />

            <Card
              icon={<FiIcons.FiShoppingBag size={22} />}
              title="عناصر المتجر"
              count={statistics.data?.shopItemsCount ?? '-'}
              iconBg={theme.secColor}
            />

            <Card
              icon={<FiIcons.FiServer size={22} />}
              title="عدد السيرفرات"
              count={statistics.data?.serversCount ?? '-'}
            />

            <Card
              icon={<FiIcons.FiRepeat size={22} />}
              title="إجمالي المعاملات"
              count={statistics.data?.transactionsCount ?? '-'}
            />

            <Card
              icon={<FiIcons.FiClock size={22} />}
              title="معاملات قيد الانتظار"
              count={statistics.data?.pendingTransactionsCount ?? '-'}
              iconBg={theme.gary1}
            />

            <Card
              icon={<FiIcons.FiTrendingUp size={22} />}
              title="إجمالي الإيرادات"
              count={statistics.data?.totalRevenue?.toFixed(2) ?? '-'}
              suffix="$"
              bg={theme.primary}
              iconBg={theme.light}
              titleColor={theme.light}
              countColor={theme.light}
              iconColor={theme.primary}
            />
          </SimpleGrid>
        </Box>
      )}

      {canViewStats && (
        <Box
          bg={theme.light}
          border={`1px solid ${theme.border}`}
          borderRadius="24px"
          boxShadow={theme.shadow}
          p={{ base: 4, md: 6 }}
          mb={6}
        >
          <Flex justify="space-between" align="center" mb={5}>
            <Box>
              <Text fontWeight="800" color={theme.dark} fontSize="18px">
                {t('general.details')}
              </Text>
              <Text color={theme.text} fontSize="13px">
                تفاصيل إضافية حول النظام والمحتوى
              </Text>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            <Card
              icon={<FiIcons.FiPackage size={22} />}
              title="عناصر قيد المراجعة"
              count={statistics.data?.pendingShopItemsCount ?? '-'}
              iconBg={theme.secColor}
            />

            <Card
              icon={<FiIcons.FiCode size={22} />}
              title="مواهب قيد المراجعة"
              count={statistics.data?.pendingTalentsCount ?? '-'}
              iconBg={theme.gary1}
            />

            <Card
              icon={<FiIcons.FiStar size={22} />}
              title="صناع المحتوى"
              count={statistics.data?.contentCreatorsCount ?? '-'}
            />

            <Card
              icon={<FiIcons.FiVideo size={22} />}
              title="الفيديوهات"
              count={statistics.data?.videosCount ?? '-'}
            />
          </SimpleGrid>
        </Box>
      )}

      {canViewStats && (
        <Box
          bg={theme.light}
          border={`1px solid ${theme.border}`}
          borderRadius="24px"
          boxShadow={theme.shadow}
          p={{ base: 4, md: 6 }}
        >
          <Flex justify="space-between" align="center" mb={5}>
            <Box>
              <Text fontWeight="800" color={theme.dark} fontSize="18px">
                أحدث المعاملات المالية
              </Text>
              <Text color={theme.text} fontSize="13px">
                آخر 5 معاملات تمت في المحفظة
              </Text>
            </Box>

            {auth.user?.role?.permissions?.includes('manage_transactions') && (
              <NavLink to="/admin/transactions">
                <Text color={theme.primary} fontWeight="700" fontSize="14px">
                  عرض الكل
                </Text>
              </NavLink>
            )}
          </Flex>

          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>المستخدم</th>
                <th>النوع</th>
                <th>المبلغ</th>
                <th>الحالة</th>
                <th>التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {statistics.data?.recentTransactions?.length ? (
                statistics.data.recentTransactions.map((tx, idx) => (
                  <tr key={tx._id}>
                    <td>{idx + 1}</td>
                    <td>{tx.user?.name ?? '-'}</td>
                    <td>{TRANSACTION_TYPE_LABEL[tx.type] ?? tx.type}</td>
                    <td>{tx.amount} {tx.currency}</td>
                    <td>
                      <Badge colorScheme={TRANSACTION_STATUS_COLOR[tx.status]}>
                        {TRANSACTION_STATUS_LABEL[tx.status] ?? tx.status}
                      </Badge>
                    </td>
                    <td>{new Date(tx.created_at).toLocaleDateString('ar-EG')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <Text color={theme.text} textAlign="center" py={4}>
                      لا توجد معاملات حتى الآن
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;